import * as React from 'react';
import classnames from 'classnames';
import ModalsFactory from '../../framework/factories/ModalsFactory';
import styles from './BaseModal.styl';
// Elements a keyboard user can actually land on — same list the WAI-ARIA APG dialog
// pattern's example focus trap uses. `[tabindex]:not([tabindex="-1"])` picks up
// deliberately-focusable non-form elements (e.g. Dropdown's popover items) without
// picking up the dialog panel's own `tabIndex={-1}` fallback-focus target.
const FOCUSABLE_SELECTOR = [
    'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
    'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])'
].join(', ');
// Modals a click outside the panel must not close: the checkout congratulations, and the guest
// offer request (LOGIN_REGISTRATION_MODAL), which closes through its X only so a stray click
// doesn't throw away the half-filled registration form.
const BACKDROP_CLICK_KEEPS_OPEN = ['CONGRATULATIONS_CHECKOUT_MODAL', 'LOGIN_REGISTRATION_MODAL'];
class BaseModal extends React.PureComponent {
    constructor() {
        super(...arguments);
        // Stable per-instance id for aria-labelledby — keyed on `type` (unique per entry in
        // ModalOverlay's `modals` Map) rather than React.useId(), since this is a class
        // component. Passed down as `titleId` to isDynamicTitle modals (LoginRegistrationModal
        // etc.) that render their own <h3> instead of BaseModal's, so the id lands on whichever
        // element actually renders the title either way.
        Object.defineProperty(this, "titleId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: `modal-title-${this.props.type}`
        });
        Object.defineProperty(this, "sheetRef", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "previouslyFocused", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "getFocusable", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => this.sheetRef ? Array.from(this.sheetRef.querySelectorAll(FOCUSABLE_SELECTOR)) : []
        });
        // Moves focus onto the first real field, not the close button — even though the close
        // button sits first in DOM order (inside the title row) — matching the explicit "focus
        // the first field" requirement rather than the more common "focus the dialog/close
        // button" default. Falls back to the close button, then the dialog panel itself
        // (tabIndex={-1} on the panel), if the modal genuinely has nothing else focusable yet
        // (e.g. still loading).
        Object.defineProperty(this, "focusFirstField", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                const focusable = this.getFocusable();
                const firstField = focusable.find((el) => !/closeButton/.test(el.className));
                const target = firstField || focusable[0] || this.sheetRef;
                // { preventScroll: true }: this runs from componentDidMount, i.e. at the very first
                // animation frame of .modalCheckout's own autoDeSheetUp entrance animation
                // (BaseModal.styl) — at that instant the sheet's actual computed transform is still the
                // keyframe's `from` state (translateY(100%), pushed off-screen below the viewport), not
                // its settled position yet. A plain .focus() on an element the browser currently
                // considers off-screen makes it auto-scroll the nearest scrollable ancestor (.backdrop,
                // itself `overflow-y: auto`) to bring it into view — fighting the CSS slide-in transform
                // that's simultaneously animating the sheet to its real position, which is exactly what
                // produced the double-motion "jumps up, then settles back down" glitch on open.
                // preventScroll skips that native auto-scroll entirely; the CSS animation alone is what
                // should be moving the sheet, so this removes the second, conflicting motion.
                if (target)
                    target.focus({ preventScroll: true });
            }
        });
        // Escape-to-close and the Tab focus trap both live on one document-level listener
        // (capture phase, so this runs before any per-field onKeyDown a form field might add)
        // rather than a focus-trap library — keeps the dependency footprint at zero for what's
        // otherwise a ~20-line implementation, and this is the only modal-wide keydown handler
        // in the codebase (confirmed via grep — no existing per-modal Escape/focus-trap logic
        // to conflict with).
        Object.defineProperty(this, "onKeyDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                const { type, onClose, showCloseButton } = this.props;
                // Only modals that actually offer a close affordance (the X button) get Escape too —
                // showCloseButton: false (CONGRATULATIONS_CHECKOUT_MODAL, QUALITY_RESTRICTION_MODAL,
                // the PricingTool modal, …) already means "don't let the user casually dismiss this,"
                // and Escape would otherwise quietly reopen that same escape hatch (pun intended).
                if (event.key === 'Escape' && showCloseButton) {
                    event.stopPropagation();
                    onClose(type);
                    return;
                }
                if (event.key !== 'Tab' || !this.sheetRef)
                    return;
                const focusable = this.getFocusable();
                if (!focusable.length)
                    return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                const active = document.activeElement;
                // wrap at the ends, and also catch focus having drifted outside the panel entirely
                // (e.g. a popover portal — see Dropdown's menuPortal — placing content in
                // document.body rather than inside sheetRef).
                if (event.shiftKey ? (active === first || !this.sheetRef.contains(active)) : (active === last || !this.sheetRef.contains(active))) {
                    event.preventDefault();
                    (event.shiftKey ? last : first).focus();
                }
            }
        });
        Object.defineProperty(this, "setSheetRef", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (node) => {
                this.sheetRef = node;
            }
        });
        // mobile-only: swipe-down-to-dismiss on the checkout sheet's grabber (fin-sheet-grip /
        // sheet-handle) — was purely decorative, no touch handlers, so it visually looked like a
        // draggable grip but couldn't actually be swiped. Same imperative-transform pattern as
        // VdpModules.tsx's finance bottom sheet (that one already has working swipe-to-dismiss on
        // its own grip): the sheet follows the finger directly via style.transform (no re-renders
        // while dragging), and released past 90px it slides out and closes, otherwise springs back.
        Object.defineProperty(this, "dragStartY", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onGrabStart", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                // sheet gesture is phone-only — the desktop centered modal (BaseModal.styl's
                // +media-tablet-up() override, $tablet = 768px) must not be draggable.
                if (typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches)
                    return;
                this.dragStartY = event.touches[0].clientY;
                if (this.sheetRef)
                    this.sheetRef.style.transition = 'none';
            }
        });
        Object.defineProperty(this, "onGrabMove", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                if (this.dragStartY == null || !this.sheetRef)
                    return;
                const dy = event.touches[0].clientY - this.dragStartY;
                // only follow downward drags — upward is not a valid "swipe to dismiss" direction, and
                // the sheet is already flush with the bottom of the viewport so there's nowhere to go.
                this.sheetRef.style.transform = dy > 0 ? `translateY(${dy}px)` : '';
            }
        });
        Object.defineProperty(this, "onGrabEnd", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                if (this.dragStartY == null || !this.sheetRef)
                    return;
                const dy = event.changedTouches[0].clientY - this.dragStartY;
                this.dragStartY = null;
                const el = this.sheetRef;
                el.style.transition = 'transform 0.2s ease-out';
                if (dy > 90) {
                    el.style.transform = 'translateY(100%)';
                    const { type, onClose } = this.props;
                    // let the slide-out play before the component actually unmounts, same 200ms lead-in
                    // VdpModules.tsx uses ahead of its own 0.25s transition.
                    window.setTimeout(() => onClose(type), 200);
                }
                else {
                    el.style.transform = '';
                }
            }
        });
        // The grip's own visible box is only ~12px tall (BaseModal.styl/md3.css sizing, tuned to
        // Figma) — too small a target to reliably grab on a real phone. Rather than inflate that
        // box (which would push the title row down / need padding compensated everywhere the grip
        // is reused), extend the SAME drag handlers onto the whole title row too, same as
        // VdpModules.tsx's finance sheet already does (dragZone on both its grip and its header) —
        // a swipe started anywhere in the header now works, not just the hairline handle. Spread
        // onto both the plain-title branch below and (via the isDynamicTitle prop pass-through)
        // LoginRegistrationModal's own <h3>. The close button living inside that same row keeps
        // working: a tap (near-zero dy) never crosses the 90px close threshold and nothing here
        // calls preventDefault, so its own onClick still fires normally after touchend.
        Object.defineProperty(this, "dragHandleProps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => ({
                onTouchStart: this.onGrabStart,
                onTouchMove: this.onGrabMove,
                onTouchEnd: this.onGrabEnd,
                style: { touchAction: 'none' }
            })
        });
        Object.defineProperty(this, "backdrop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onBackDropClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                if (event.target === this.backdrop) {
                    event.stopPropagation();
                    event.nativeEvent.stopImmediatePropagation();
                    const { type, onClose } = this.props;
                    onClose(type);
                }
            }
        });
        Object.defineProperty(this, "onCloseClick", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                event.stopPropagation();
                event.nativeEvent.stopImmediatePropagation();
                const { type, onClose } = this.props;
                onClose(type);
            }
        });
        Object.defineProperty(this, "setReference", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (node) => {
                this.backdrop = node;
            }
        });
    }
    UNSAFE_componentWillMount() {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
    }
    componentDidMount() {
        // remember what had focus (the trigger button, typically) so it can be restored on
        // close — WAI-ARIA APG dialog pattern §"focus management".
        this.previouslyFocused = document.activeElement;
        this.focusFirstField();
        document.addEventListener('keydown', this.onKeyDown, true);
    }
    componentWillUnmount() {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.removeEventListener('keydown', this.onKeyDown, true);
        if (this.previouslyFocused && document.contains(this.previouslyFocused)) {
            this.previouslyFocused.focus();
        }
    }
    render() {
        const { type, props, showCloseButton, className, overlayClassName, closeButtonClassName, title, isCheckoutViewModal, isDynamicTitle, titleClassName, contentClassName } = this.props;
        return (React.createElement("div", { className: classnames(styles.backdrop, overlayClassName), onMouseDown: !BACKDROP_CLICK_KEEPS_OPEN.includes(type) ? this.onBackDropClick : () => { }, ref: this.setReference }, !isCheckoutViewModal ? (React.createElement("div", { className: classnames(styles.modal, className), role: "dialog", "aria-modal": "true", "aria-label": title || undefined, ref: this.setSheetRef, tabIndex: -1 },
            ModalsFactory[type](props),
            showCloseButton ? React.createElement("span", { className: classnames(styles.closeButton, closeButtonClassName), onClick: this.onCloseClick }) : null)) : (React.createElement("div", { className: classnames(styles.modalCheckout, className), role: "dialog", "aria-modal": "true", "aria-labelledby": this.titleId, ref: this.setSheetRef, tabIndex: -1 },
            React.createElement("div", { className: "fin-sheet-grip show-mobile", ...this.dragHandleProps() },
                React.createElement("span", { className: "sheet-handle", style: { margin: 0 } })),
            isDynamicTitle ? (ModalsFactory[type]({
                ...props,
                showCloseButton,
                onCloseModal: this.onCloseClick,
                closeButtonClassName,
                titleId: this.titleId,
                dragHandleProps: this.dragHandleProps()
            })) : (React.createElement(React.Fragment, null,
                React.createElement("h3", { id: this.titleId, className: classnames(styles.title, titleClassName), ...this.dragHandleProps() },
                    title,
                    showCloseButton ? (React.createElement("button", { type: "button", "aria-label": "close", className: classnames(styles.closeButtonCheckout, closeButtonClassName), onClick: this.onCloseClick })) : null),
                React.createElement("div", { className: classnames(styles.content, contentClassName) }, ModalsFactory[type](props))))))));
    }
}
Object.defineProperty(BaseModal, "defaultProps", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: {
        type: '',
        props: {},
        showCloseButton: true,
        isCheckoutViewModal: false,
        isDynamicTitle: false,
        onClose: () => { }
    }
});
export default BaseModal;
